import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatLogComponent } from '../chat-log/chat-log.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { DxButtonModule, DxPopupComponent, DxPopupModule, DxTemplateModule, DxToolbarModule } from 'devextreme-angular';
import { ChatMessage } from '../models/chat-message.model';
import { ChatService } from '../actions/chat.service';
import { DxoLabelModule } from 'devextreme-angular/ui/nested';
import { ChatMessageFromEnum } from '../models/chat-message-from.enum';
import { LocalstorageService } from '@nida-web/shared/local-storage';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'nida-web-chat',
  imports: [
    CommonModule,
    ChatLogComponent,
    ChatInputComponent,
    DxPopupModule,
    DxTemplateModule,
    DxToolbarModule,
    DxButtonModule,
    DxoLabelModule,
    TranslocoPipe,
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements AfterViewInit {
  @ViewChild('chatPopup') popupRef: DxPopupComponent;

  private readonly lsKeyMuted = 'chat_muted';
  private readonly lsKeyOpen = 'chat_open';

  protected chatMessages: ChatMessage[] = [];
  protected connectionEstablished = false;
  protected muted = false;
  protected open = true;

  constructor(private chatService: ChatService, private localStorageService: LocalstorageService) {
    if (ChatService.hasChat()) {
      this.chatService.getConnectionObservable().subscribe((connected) => {
        this.connectionEstablished = connected;
      });

      this.chatService.getChatObservable().subscribe((chatMessages: ChatMessage[]) => {
        const isInitialCall = this.chatMessages.length === 0;
        this.chatMessages = chatMessages;
        const lastMessage = chatMessages.slice(-1)[0];

        if (lastMessage.from !== ChatMessageFromEnum.ME && !this.muted) {
          // dont remove the class when the popup is collapsed, so that it wont flicker
          if (this.open) {
            // remove the class to trigger the animation again when adding it
            this.popupRef.instance.content().parentElement?.classList.remove('new-message');
          }

          if (!isInitialCall) {
            setTimeout(() => {
              this.popupRef.instance.content().parentElement?.classList.add('new-message');
            }, 100);
          }
        }
      });
    }
  }

  public ngAfterViewInit() {
    this.muted = this.localStorageService.receiveValueByKeySafe(this.lsKeyMuted) === 'true';
    this.open = this.localStorageService.receiveValueByKeySafe(this.lsKeyOpen) !== 'false';
  }

  protected onSendMessage(message: string) {
    if (!message) {
      return;
    }

    this.chatService.sendChatMessage(message);
  }

  protected toggle() {
    this.open = !this.open;
    this.localStorageService.saveKeyAndValue(this.lsKeyOpen, this.open);
    this.execToggle();
  }

  private execToggle() {
    const popupWrapper = this.popupRef?.instance?.content()?.parentElement as HTMLElement;

    if (!popupWrapper) {
      return;
    }

    popupWrapper.classList.remove('new-message');

    if (this.open) {
      popupWrapper.classList.remove('collapse');
    } else {
      popupWrapper.classList.add('collapse');
    }

    // Check the position of the popup after expanding it and move it fully into the viewport so that no part of the
    // popup will be out of view if it was opened next to the bottom or right edge.
    const availableHeight = window.innerHeight;
    const availableWidth = window.innerWidth;
    const containerBounds = popupWrapper.getBoundingClientRect();
    const containerHeight = containerBounds.height;
    const containerWidth = containerBounds.width;
    const transformElements = popupWrapper.style.transform.match(/translate\((\d+)px, (\d+)px\)/) || [];
    const transformX = !isNaN(parseFloat(transformElements[1])) ? parseFloat(transformElements[1]) : 0;
    const transformY = !isNaN(parseFloat(transformElements[2])) ? parseFloat(transformElements[2]) : 0;
    const newY = transformY + containerHeight > availableHeight ? availableHeight - containerHeight : transformY;
    const newX = transformX + containerWidth > availableWidth ? availableWidth - containerWidth : transformX;
    popupWrapper.style.transform = `translate(${newX}px, ${newY}px)`;
  }

  protected mute() {
    this.muted = !this.muted;
    this.localStorageService.saveKeyAndValue(this.lsKeyMuted, this.muted);

    if (this.muted) {
      this.popupRef.instance.content().parentElement?.classList.remove('new-message');
    }
  }

  protected popupReady(event) {
    // Hide the popup initially, so that no flickering is visible
    event.component.content().parentElement.classList.add('chat-popup', 'hidden');

    // Show popup after 500ms as a fallback when there are no messages in the mqtt topic. Usually it should be shown,
    // once the messages are loaded.
    setTimeout(() => {
      this.onInitialScroll();
    }, 500);
  }

  /**
   * Will be called once the messages from the MQTT server were loaded (by the initialScroll event) or, if no messages
   * are stored, 500ms after the popup was rendered.
   * @protected
   */
  protected onInitialScroll() {
    if (this.popupRef.instance.content().parentElement) {
      // Execute the toggle to set the css classes properly
      this.execToggle();
      // After the popup was initially toggled based on the local storage value, it can finally be shown, once all
      // the resizing and positioning is done
      this.popupRef.instance.content().parentElement?.classList.remove('hidden');
    }
  }
}
